import { useState } from "react";
import { normalizeActivityMapping } from "./helpManualData";

const SCOPES = [
  { id: "enterprise", label: "Enterprise", clientTypeId: 1 },
  { id: "site", label: "Site", clientTypeId: 2 },
];

const MappingRowLabel = ({ code, name }) => (
  <span className="help-manual-mapping__copy">
    <span className="help-manual-mapping__code">{code}</span>
    <span className="help-manual-mapping__name">{name}</span>
  </span>
);

/**
 * @param {{ selectedIds: object, onChange: Function, modules?: Array, emptyMessage?: string }} props
 */
const ModuleActivityMapping = ({
  selectedIds,
  onChange,
  modules = [],
  emptyMessage = "No modules or activities available.",
}) => {
  const [scope, setScope] = useState("enterprise");
  const mapping = normalizeActivityMapping(selectedIds);
  const selectedSet = new Set(mapping[scope].map(String));
  const activeScope = SCOPES.find((s) => s.id === scope) || SCOPES[0];
  const tree = (Array.isArray(modules) ? modules : [])
    .filter((module) => {
      const typeId = module.clientTypeId;
      return typeId == null || Number(typeId) === activeScope.clientTypeId;
    })
    // Topic mapping stores ActivityIds only — modules with no activities cannot be selected.
    .filter((module) => (module.activities || []).length > 0);

  const emit = (nextScopeIds) => {
    onChange({
      ...mapping,
      [scope]: nextScopeIds.map(String),
    });
  };

  const toggleActivity = (activityId) => {
    const id = String(activityId);
    const next = new Set(selectedSet);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    emit([...next]);
  };

  const toggleModule = (module) => {
    const moduleActivityIds = module.activities.map((a) => String(a.id));
    const allSelected = moduleActivityIds.every((id) => selectedSet.has(id));
    const next = new Set(selectedSet);
    if (allSelected) {
      moduleActivityIds.forEach((id) => next.delete(id));
    } else {
      moduleActivityIds.forEach((id) => next.add(id));
    }
    emit([...next]);
  };

  return (
    <div className="help-manual-mapping">
      <div className="help-manual-mapping__header">
        <div className="help-manual-mapping__tabs" role="tablist">
          {SCOPES.map((item) => (
            <button
              key={item.id}
              type="button"
              role="tab"
              aria-selected={scope === item.id}
              className={`help-manual-mapping__tab ${
                scope === item.id ? "is-active" : ""
              }`}
              onClick={() => setScope(item.id)}
            >
              {item.label}
            </button>
          ))}
        </div>
        <span className="help-manual-mapping__count">
          {mapping[scope].length} selected
        </span>
      </div>

      <div className="max-h-[320px] overflow-y-auto table-scroll">
        {tree.length === 0 ? (
          <p className="text-sm text-gray-500 px-2 py-3">{emptyMessage}</p>
        ) : (
          tree.map((module) => {
            const moduleIds = module.activities.map((a) => String(a.id));
            const allSelected =
              moduleIds.length > 0 &&
              moduleIds.every((id) => selectedSet.has(id));
            const someSelected =
              !allSelected && moduleIds.some((id) => selectedSet.has(id));
            const displayCode = module.code || module.id;

            return (
              <div key={module.id} className="help-manual-mapping__module">
                <label className="help-manual-mapping__module-row cursor-pointer">
                  <input
                    type="checkbox"
                    className="accent-primary cursor-pointer"
                    checked={allSelected}
                    ref={(el) => {
                      if (el) el.indeterminate = someSelected;
                    }}
                    onChange={() => toggleModule(module)}
                  />
                  <MappingRowLabel code={displayCode} name={module.name} />
                </label>
                {module.activities.map((activity) => {
                  const activityId = String(activity.id);
                  const selected = selectedSet.has(activityId);
                  return (
                    <label
                      key={activityId}
                      className={`help-manual-mapping__activity ${
                        selected
                          ? "help-manual-mapping__activity--selected"
                          : ""
                      }`}
                    >
                      <input
                        type="checkbox"
                        className="accent-primary cursor-pointer"
                        checked={selected}
                        onChange={() => toggleActivity(activityId)}
                      />
                      <MappingRowLabel
                        code={activity.code || activityId}
                        name={activity.name}
                      />
                    </label>
                  );
                })}
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};

export default ModuleActivityMapping;
